import { IDropdownOption, MessageBarType } from '@fluentui/react';
import {
  Button,
  buttonStylesGhost,
  buttonStylesPrimary,
  Dropdown,
  FileUpload,
  Loader,
  LoaderType,
  MessageBarList,
  messageBarListStylesToast,
  TextField,
  useClassNames,
  useMessageBarList,
  useToast,
} from '@h2oai/ui-kit';
import { useCallback, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import {
  ArtifactUploadStatus,
  CoreService,
  StoreFileArtifactRequest,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { buttonStylesPrimaryPanel } from '../../featurestore/models';
import { getErrorMessageBarMessage, getMd5Checksum } from '../../featurestore/utils';
import { IUploadArtifactClassNames, IUploadArtifactStyles, uploadArtifactStylesDefault } from './UploadArtifact.styles';

enum UploadType {
  File = 'File',
  Link = 'Link',
}

const uploadTypeOptions: IDropdownOption[] = [
  { key: UploadType.File, text: UploadType.File },
  { key: UploadType.Link, text: UploadType.Link },
];

interface ArtifactInfo extends StoreFileArtifactRequest {
  file?: File;
}

interface IUploadArtifactProps {
  featureSetId?: string;
  featureSetMajorVersion?: string;
  onSave?: () => void;
  onCancel?: () => void;
}

export const UploadArtifact = ({ featureSetId, featureSetMajorVersion, onSave, onCancel }: IUploadArtifactProps) => {
  const [values, setValues] = useState<ArtifactInfo>({
    featureSetId,
    featureSetMajorVersion: featureSetMajorVersion !== undefined ? parseInt(featureSetMajorVersion) : undefined,
  });
  const [uploadType, setUploadType] = useState<UploadType>(UploadType.File);
  const [uploading, setUploading] = useState(false);
  const { addToast } = useToast();
  const { messages, addMessage, removeMessage } = useMessageBarList();
  const onUpload = useCallback(
    async (values: ArtifactInfo) => {
      let uploadingArtifactId = '';
      setUploading(true);
      try {
        if (uploadType === UploadType.File) {
          const { file, ...req } = values;
          const { artifactId, url, headers } = await fsApi(CoreService.StoreFileArtifact, req);
          uploadingArtifactId = artifactId;
          await fsApi(CoreService.UpdateUploadStatus, {
            artifactId,
            uploadStatus: ArtifactUploadStatus.ARTIFACT_UPLOAD_STATUS_IN_PROGRESS,
          });
          const res = await fetch(url, {
            method: 'PUT',
            body: file,
            headers,
          });
          if (!res.ok) {
            throw res;
          }
          await fsApi(CoreService.UpdateUploadStatus, {
            artifactId,
            uploadStatus: ArtifactUploadStatus.ARTIFACT_UPLOAD_STATUS_DONE,
          });
        } else {
          const { file, filename, md5Checksum, ...req } = values;
          await fsApi(CoreService.StoreLink, req);
        }
        addToast({
          messageBarType: MessageBarType.success,
          message: 'Artifact is Uploaded Successfully!',
        });
      } catch (err) {
        if (uploadingArtifactId) {
          await fsApi(CoreService.UpdateUploadStatus, {
            artifactId: uploadingArtifactId,
            uploadStatus: ArtifactUploadStatus.ARTIFACT_UPLOAD_STATUS_FAILED,
          });
        }
        const { messageBarType, message } = getErrorMessageBarMessage(
          err,
          'An error has occurred while uploading the artifact: '
        );
        addMessage(message as string, undefined, undefined, messageBarType);
      } finally {
        setUploading(false);
        onSave && onSave();
      }
    },
    [uploadType]
  );
  const classNames = useClassNames<IUploadArtifactStyles, IUploadArtifactClassNames>(
    'UploadArtifact',
    uploadArtifactStylesDefault
  );
  return (
    <div className={classNames.root} data-test="upload-artifact">
      <div className={classNames.messageBar}>
        <MessageBarList
          styles={messageBarListStylesToast}
          messages={messages}
          autoClose
          groupByMessage
          onRemoveMessage={removeMessage}
        />
      </div>
      <div className={classNames.form}>
        <div className={classNames.fields}>
          <TextField
            name="title"
            label="Title"
            required
            onChange={(_e, title) => {
              setValues({ ...values, title });
            }}
          />
          <TextField
            name="description"
            label="Description"
            multiline
            rows={3}
            onChange={(_e, description) => {
              setValues({ ...values, description });
            }}
          />
          <Dropdown
            label="Type"
            placeholder="Select Upload Type"
            selectedKey={uploadType}
            options={uploadTypeOptions}
            onChange={(_e, option) => {
              if (option) {
                const uploadType = option.key as UploadType;
                setUploadType(uploadType);
                if (uploadType === UploadType.File) {
                  setValues({ ...values, url: '' });
                } else {
                  setValues({ ...values, filename: '', md5Checksum: '' });
                }
              }
            }}
          />
          {uploadType === UploadType.File ? (
            <FileUpload
              uploadCallback={(files: File[]) => {
                if (files[0]) {
                  const filename = files[0].name;
                  const reader = new FileReader();
                  reader.onloadend = (e) => {
                    const md5Checksum = getMd5Checksum(e);
                    if (md5Checksum) {
                      setValues({ ...values, filename, md5Checksum, file: files[0] });
                    }
                  };
                  reader.readAsArrayBuffer(files[0]);
                }
              }}
              maxFileSize={2e8}
              maxSize={2e8}
              fileExtensions={['csv', 'json', 'pdf', 'ppt', 'doc', 'xlsx', 'video']}
              data-test="upload-artifact-file-upload"
            />
          ) : (
            <TextField
              name="url"
              label="Url"
              required
              onChange={(_e, url) => {
                setValues({ ...values, url });
              }}
            />
          )}
          {uploading && <Loader label="Uploading a file" type={LoaderType.progressIndicator} />}
        </div>
        <div className={classNames.buttons}>
          <Button styles={buttonStylesGhost} text="Cancel" onClick={onCancel} style={{ marginRight: 8 }} />
          <Button
            styles={[buttonStylesPrimary, buttonStylesPrimaryPanel]}
            onClick={() => onUpload(values)}
            text="Save"
            disabled={
              !values.title ||
              (uploadType === UploadType.File && !values.filename) ||
              (uploadType === UploadType.Link && !values.url)
            }
          />
        </div>
      </div>
    </div>
  );
};
